<template>
  <div>
    <PageTitleVue> PRÉ-VISUALIZAR RELATÓRIO </PageTitleVue>

    <section class="space-y-5">
      <!-- header -->
      <div
        class="no-print rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
      >
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
        >
          <div>
            <button
              type="button"
              class="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-[#2f8f2f]"
              @click="goBack"
            >
              <PhArrowLeft class="h-4 w-4" />
              Voltar para montagem
            </button>

            <div v-if="report">
              <div class="flex flex-wrap items-center gap-3">
                <h1 class="text-2xl font-bold text-gray-900">
                  {{ report.title }}
                </h1>

                <ReportStatusBadge :status="report.status" />
              </div>

              <p class="mt-1 text-sm text-gray-500">
                {{ clientName }} • {{ formatDate(report.visit_date) }} •
                {{ layoutName }}
              </p>
            </div>

            <div v-else class="space-y-2">
              <div class="h-7 w-72 animate-pulse rounded-lg bg-gray-100"></div>
              <div class="h-4 w-96 animate-pulse rounded-lg bg-gray-100"></div>
            </div>
          </div>

          <div v-if="report" class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
              @click="goBack"
            >
              <PhWrench class="h-5 w-5" />
              Editar conteúdo
            </button>

            <button
              v-if="report.status === 'finished'"
              type="button"
              class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="isGeneratingPdf"
              @click="generatePdf"
            >
              <PhCircleNotch
                v-if="isGeneratingPdf"
                class="h-5 w-5 animate-spin"
              />
              <PhFilePdf v-else class="h-5 w-5" />
              Gerar PDF oficial
            </button>

            <button
              v-if="report.status === 'issued' && getPdfUrl(report)"
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border border-green-100 bg-white px-4 py-2.5 text-sm font-semibold text-[#2f8f2f] shadow-sm transition hover:bg-green-50"
              @click="openPdf"
            >
              <PhFilePdf class="h-5 w-5" />
              Abrir PDF
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="report && !hasLayout"
        class="no-print rounded-2xl border border-yellow-100 bg-yellow-50/70 p-4"
      >
        <div class="flex gap-3">
          <div
            class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-yellow-600 shadow-sm"
          >
            <PhWarningCircle class="h-5 w-5" />
          </div>

          <div>
            <h3 class="text-sm font-bold text-yellow-800">
              Relatório sem layout vinculado
            </h3>

            <p class="mt-1 text-sm text-yellow-700">
              Este relatório será exibido com um modelo básico. Para aplicar
              capa, cabeçalho, rodapé e áreas dinâmicas, vincule um layout ao
              relatório.
            </p>
          </div>
        </div>
      </div>

      <!-- loading preview -->
      <div
        v-if="isLoading"
        class="no-print rounded-2xl bg-white p-8 text-center shadow-sm"
      >
        <PhCircleNotch class="mx-auto h-8 w-8 animate-spin text-[#2f8f2f]" />
        <p class="mt-3 text-sm text-gray-500">Carregando prévia...</p>
      </div>

      <!-- preview -->
      <div v-else-if="report" class="preview-shell">
        <div class="preview-pages">
          <article class="report-page cover-page">
            <template v-if="coverSrc">
              <img
                :src="coverSrc"
                :alt="layoutName"
                class="absolute inset-0 h-full w-full object-cover"
              />

              <div
                v-for="dynamicArea in coverDynamicAreas"
                :key="dynamicArea.key"
                class="absolute flex items-center justify-center overflow-visible"
                :style="getAreaStyle(dynamicArea.area)"
              >
                <div
                  class="flex h-full w-full items-center justify-center overflow-hidden"
                  :style="getAreaContentFrameStyle(dynamicArea)"
                >
                  <img
                    v-if="dynamicArea.type === 'image' && dynamicArea.value"
                    :src="dynamicArea.value"
                    :alt="clientName"
                    class="h-full w-full"
                    :style="getImageAreaStyle(dynamicArea)"
                    draggable="false"
                  />

                  <span
                    v-else-if="dynamicArea.type === 'text'"
                    class="line-clamp-4 px-2"
                    :style="getTextAreaStyle(dynamicArea)"
                  >
                    {{ dynamicArea.value }}
                  </span>
                </div>
              </div>
            </template>

            <template v-else>
              <div
                class="flex h-full flex-col items-center justify-center p-12 text-center"
                :style="fallbackCoverStyle"
              >
                <p
                  class="text-sm font-semibold uppercase tracking-[0.3em] text-white/80"
                >
                  Relatório Técnico
                </p>

                <h2 class="mt-6 text-4xl font-bold text-white">
                  {{ report.title }}
                </h2>

                <p class="mt-4 text-lg text-white/90">
                  {{ clientName }}
                </p>

                <p class="mt-2 text-sm text-white/70">
                  {{ formatDate(report.visit_date) }}
                </p>
              </div>
            </template>
          </article>
          
          <article class="report-page content-document">
            <img
              v-if="watermarkSrc"
              :src="watermarkSrc"
              alt="Marca d’água"
              class="watermark"
            />

            <div v-if="headerSrc" class="page-header-image">
              <img :src="headerSrc" alt="Cabeçalho" />
            </div>

            <div v-else class="page-header-basic">
              <div>
                <p
                  class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400"
                >
                  Relatório de Visita Técnica
                </p>

                <h3 class="text-xs font-bold text-gray-900">
                  {{ report.title }}
                </h3>
              </div>

              <div class="text-right text-xs text-gray-500">
                <p>{{ clientName }}</p>
                <p>{{ formatDate(report.visit_date) }}</p>
              </div>

              <!-- aqui vai a logo da empresa melhor formatada e ajustada -->
              <!-- <img 
                class="w-20 h-10"
                :src="clientLogoSrc" 
                alt=""
              > -->
            </div>

            <main class="flow-content">
              <section class="report-intro">
                
                <!-- hedaer flow -->
                <div>
                  <p
                    class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400"
                  >
                    <!-- remover/adaptar -->
                    Corpo técnico do relatório
                  </p>

                  <h2 class="mt-2 text-2xl font-bold text-gray-900">
                    Pontos observados durante a visita
                  </h2>

                  <p class="mt-2 text-sm leading-relaxed text-gray-500 text-start">
                    {{ 
                      // 'here' ?? 
                      'Abaixo estão descritos os pontos identificados, observações técnicas, recomendações e registros fotográficos da visita técnica.'
                    }}
                  </p>
                </div>

                <div class="intro-meta">
                  <div>
                    <span>Empresa</span>
                    <strong>{{ clientName }}</strong>
                  </div>

                  <div>
                    <span>Data da visita</span>
                    <strong>{{ formatDate(report.visit_date) }}</strong>
                  </div>

                  <div>
                    <span>Técnico responsável</span>
                    <strong>{{ report.technician_name || "-" }}</strong>
                  </div>
                </div>
              </section>

              <section v-if="items.length" class="report-items-flow">
                <article
                  v-for="(item, index) in items"
                  :key="item.id"
                  :class="[
                    'report-item',
                    isLargeItem(item) ? 'report-item-large' : '',
                  ]"
                >
                  <div class="item-header">
                    <div class="text-start">
                      <span
                        class="inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase text-gray-500"
                        :style="chipStyle"
                      >
                        Item {{ index + 1 }}
                      </span>

                      <h2 class="mt-3 text-xl font-bold text-gray-900">
                        {{ item.title || "Item sem título" }}
                      </h2>

                      <div
                        class="mt-2 flex flex-wrap gap-2 text-xs text-gray-500"
                      >
                        <span v-if="item.sector">Setor: {{ item.sector }}</span>
                        <span v-if="item.location"
                          >Local: {{ item.location }}</span
                        >
                      </div>
                    </div>

                    <div class="flex flex-col items-end gap-2">
                      <div v-show="item.risk_level" class="flex">
                        <span class="text-[8px] font-semibold uppercase tracking-[0.2em] text-gray-400 mt-1.5">
                          Risco:
                        </span>
                        <span
                          v-if="item.risk_level"
                          class="rounded-full px-2 py-1 text-xs font-bold uppercase"
                          :class="getRiskClass(item.risk_level)"
                        >
                          {{ getRiskLabel(item.risk_level) }}
                        </span>
                      </div>

                      <div v-show="item.priority" class="flex">
                        <span class="text-[8px] font-semibold uppercase tracking-[0.2em] text-gray-400 mt-1">
                          Prioridade: 
                        </span>
                        <span
                          v-if="item.priority"
                          class="rounded-full bg-blue-50 px-2 py-1 text-xs font-bold text-blue-700 uppercase"
                          :class="getPriorityLabel(item.priority)"
                        >
                          {{ getPriorityLabel(item.priority) }}
                        </span>
                      </div>

                    </div>
                  </div>

                  <div v-if="getItemPhotoSrc(item)" class="item-photo">
                    <img
                      :src="getItemPhotoSrc(item)"
                      :alt="item.title || 'Foto do item'"
                    />
                  </div>

                  <div class="item-text-content text-start">
                    <section v-if="item.description">
                      <h4 class="section-title">Observação</h4>
                      <p class="whitespace-pre-line">{{ item.description }}</p>
                    </section>

                    <section v-if="item.improvement_point">
                      <h4 class="section-title">Ponto de melhoria</h4>
                      <p class="whitespace-pre-line">
                        {{ item.improvement_point }}
                      </p>
                    </section>

                    <section v-if="item.recommendation">
                      <h4 class="section-title">Recomendação</h4>
                      <p class="whitespace-pre-line">
                        {{ item.recommendation }}
                      </p>
                    </section>

                    <section v-if="item.technical_observation">
                      <h4 class="section-title">Observação técnica</h4>
                      <p class="whitespace-pre-line">
                        {{ item.technical_observation }}
                      </p>
                    </section>
                  </div>
                </article>
              </section>

              <section v-else class="empty-content">
                <PhWarningCircle class="mx-auto h-12 w-12 text-gray-300" />

                <h2 class="mt-4 text-xl font-bold text-gray-800">
                  Nenhum item técnico cadastrado
                </h2>

                <p class="mt-2 text-sm text-gray-500">
                  Volte para a montagem do relatório e adicione os pontos
                  observados.
                </p>
              </section>
            </main>

            <div v-if="footerSrc" class="page-footer-image">
              <img :src="footerSrc" alt="Rodapé" />
            </div>

            <div v-else class="page-footer-basic">
              <span>{{ clientName }}</span>
              <span>Relatório técnico</span>
            </div>
          </article>

          <article v-if="!items.length" class="report-page content-page">
            <main
              class="flex h-full items-center justify-center p-10 text-center"
            >
              <div>
                <PhWarningCircle class="mx-auto h-12 w-12 text-gray-300" />

                <h2 class="mt-4 text-xl font-bold text-gray-800">
                  Nenhum item técnico cadastrado
                </h2>

                <p class="mt-2 text-sm text-gray-500">
                  Volte para a montagem do relatório e adicione os pontos
                  observados.
                </p>
              </div>
            </main>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import protegeLogo from "@/assets/logo/protege.png";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  PhArrowLeft,
  PhCircleNotch,
  PhPrinter,
  PhWarningCircle,
  PhWrench,
  PhFilePdf,
} from "@phosphor-icons/vue";

import PageTitleVue from "@/components/Titles/PageTitle.vue";
import { showToast as toast } from "@/helpers/UseToasts";

import ReportsService from "./services/reports.service";
import ReportVisitItemsService from "./services/reportVisitItems.service";

import ReportStatusBadge from "./components/reports/ReportStatusBadge.vue";

const route = useRoute();
const router = useRouter();

const report = ref(null);
const items = ref([]);
const isLoading = ref(true);
const isGeneratingPdf = ref(false);

const reportId = computed(() => route.params.id);

const effectiveLayout = computed(() => {
  if (!report.value) return null;

  if (report.value.status === "draft") {
    return report.value.layout || report.value.layout_snapshot || null;
  }

  return report.value.layout_snapshot || report.value.layout || null;
});

const clientSnapshot = computed(() => {
  return report.value?.client_snapshot || report.value?.client || {};
});

const hasLayout = computed(() => Boolean(effectiveLayout.value));

const layoutName = computed(() => {
  return effectiveLayout.value?.name || "Modelo básico";
});

const clientName = computed(() => {
  return (
    clientSnapshot.value?.trade_name ||
    clientSnapshot.value?.corporate_name ||
    "Empresa não identificada"
  );
});

const clientLogoSrc = computed(() => {
  return (
    clientSnapshot.value?.logo_url ||
    buildFileUrl(clientSnapshot.value?.logo_path) ||
    protegeLogo
  );
});

const coverSrc = computed(() => getLayoutAsset("cover"));
const headerSrc = computed(() => getLayoutAsset("header"));
const footerSrc = computed(() => getLayoutAsset("footer"));
const watermarkSrc = computed(() => getLayoutAsset("watermark"));

const fallbackCoverStyle = computed(() => {
  const primary = effectiveLayout.value?.primary_color || "#2f8f2f";
  const secondary = effectiveLayout.value?.secondary_color || "#5dbb45";

  return {
    background: `linear-gradient(135deg, ${primary}, ${secondary})`,
  };
});

const chipStyle = computed(() => {
  const primary = effectiveLayout.value?.primary_color || "#2f8f2f";

  return {
    backgroundColor: `${primary}15`,
    color: primary,
  };
});

const coverDynamicAreas = computed(() => {
  const layout = effectiveLayout.value;

  if (!layout) return [];

  const fixedAreas = [
    {
      key: "logo_area",
      type: "image",
      area: getDynamicArea("logo_area"),
      value: clientLogoSrc.value,
    },
    {
      key: "client_name_area",
      type: "text",
      area: getDynamicArea("client_name_area"),
      value: clientName.value,
    },
    {
      key: "client_document_area",
      type: "text",
      area: getDynamicArea("client_document_area"),
      value: clientSnapshot.value?.cnpj || "",
    },
    {
      key: "visit_date_area",
      type: "text",
      area: getDynamicArea("visit_date_area"),
      value: formatDate(report.value?.visit_date),
    },
    {
      key: "technician_name_area",
      type: "text",
      area: getDynamicArea("technician_name_area"),
      value: report.value?.technician_name || "",
    },
  ].map((area) => ({
    ...area,
    config: getCoverElementConfig(area.key, area.type),
  }));

  const customFields = getCoverCustomFields();

  return [...fixedAreas, ...customFields].filter((item) => {
    return item.area && item.value && item.config?.visible !== false;
  });
});

onMounted(async () => {
  await loadPreview();
});

async function loadPreview() {
  isLoading.value = true;

  try {
    const [reportResponse, itemsResponse] = await Promise.all([
      ReportsService.getById(reportId.value),
      ReportVisitItemsService.getAll(reportId.value),
    ]);

    report.value = reportResponse?.data?.data || reportResponse?.data || null;
    items.value = itemsResponse?.data?.data || itemsResponse?.data || [];
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível carregar a pré-visualização do relatório.",
      "error"
    );

    goBack();
  } finally {
    isLoading.value = false;
  }
}

function getLayoutAsset(type) {
  const layout = effectiveLayout.value;

  if (!layout) return "";

  return layout[`${type}_url`] || buildFileUrl(layout[`${type}_path`]);
}

function getDynamicArea(key) {
  const layout = effectiveLayout.value;

  if (!layout) return null;

  const directAreas = ["logo_area", "client_name_area", "client_document_area"];

  const area = directAreas.includes(key)
    ? layout[key]
    : layout?.settings?.dynamic_areas?.[key];

  return normalizeArea(area);
}

function normalizeArea(area) {
  if (!area || typeof area !== "object") return null;

  return {
    x: clamp(Number(area.x ?? 0), -100, 200),
    y: clamp(Number(area.y ?? 0), -100, 200),
    width: clamp(Number(area.width ?? 1), 1, 200),
    height: clamp(Number(area.height ?? 1), 1, 200),
    unit: "percent",
  };
}

function getAreaStyle(area) {
  return {
    left: `${area.x}%`,
    top: `${area.y}%`,
    width: `${area.width}%`,
    height: `${area.height}%`,
  };
}

function getTextAreaStyle(dynamicArea) {
  const style = dynamicArea.config?.style || {};

  return {
    width: "100%",
    color: style.color || effectiveLayout.value?.primary_color || "#111827",
    fontFamily: style.fontFamily || "Arial, Helvetica, sans-serif",
    fontSize: `${style.fontSize || 14}px`,
    fontWeight: style.fontWeight || "700",
    textAlign: style.textAlign || "center",
    lineHeight: String(style.lineHeight || 1.15),
    letterSpacing: `${style.letterSpacing || 0}px`,
    textTransform: style.textTransform || "none",
    opacity: String(style.opacity ?? 1),
  };
}

function getItemPhotoSrc(item) {
  return item.photo_url || buildFileUrl(item.photo_path);
}

function buildFileUrl(path) {
  if (!path) return "";

  const baseUrl =
    process.env.VUE_APP_IMAGE_SERVE || process.env.VUE_APP_FILES_URL || process.env.VUE_APP_BASE_FILES_URL || "";

  if (!baseUrl) return "";

  return `${baseUrl.replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
}

function clamp(value, min, max) {
  return Math.min(Math.max(Number(value || 0), min), max);
}

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("pt-BR").format(date);
}

function getRiskLabel(level) {
  const labels = {
    low: "Baixo",
    medium: "Moderado",
    substantial: "Substancial",
    serious: "Grave",
    critical: "Crítico",
    health_risk: "Risco à saúde",
    legal_risk: "Risco legal",
  };

  return labels[level] || "Risco";
}

function getPriorityLabel(priority) {
  const labels = {
    low: "Baixa",
    medium: "Média",
    high: "Alta",
    urgent: "Urgente",
  };

  return labels[priority] || "Prioridade";
}

function getRiskClass(level) {
  const classes = {
    low: "bg-gray-100 text-gray-600",
    medium: "bg-yellow-50 text-yellow-700",
    substantial: "bg-orange-50 text-orange-700",
    serious: "bg-red-50 text-red-700",
    critical: "bg-red-100 text-red-800",
    health_risk: "bg-purple-50 text-purple-700",
    legal_risk: "bg-blue-50 text-blue-700",
  };

  return classes[level] || "bg-gray-100 text-gray-600";
}

function goBack() {
  router.push({
    name: "reports-visit-builder",
    params: {
      id: reportId.value,
    },
  });
}

function isLargeItem(item) {
  const totalTextLength = [
    item.description,
    item.improvement_point,
    item.recommendation,
    item.technical_observation,
  ]
    .filter(Boolean)
    .join(" ")
    .length;

  return totalTextLength > 1200;
}

async function generatePdf() {
  const confirmGenerate = window.confirm(
    "Deseja gerar o PDF oficial deste relatório? Após a geração, o status será alterado para emitido."
  );

  if (!confirmGenerate) return;

  isGeneratingPdf.value = true;

  try {
    // Futuro / VPS:
    await generateBackPdf();

    // Atual / servidor Apache:
    // await generateFrontPdf();
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      error?.response?.data?.message || "Não foi possível gerar o PDF.",
      "error"
    );
  } finally {
    isGeneratingPdf.value = false;
  }
}

async function generateBackPdf() {
  const response = await ReportsService.generatePdf(reportId.value);

  report.value = response?.data?.data || response?.data || report.value;

  toast(
    "Protege informa:",
    "PDF oficial gerado com sucesso.",
    "success"
  );
}

function getPdfUrl(currentReport) {
  return currentReport?.pdf_url || buildFileUrl(currentReport?.pdf_path);
}

function openPdf() {
  const url = getPdfUrl(report.value);

  if (!url) {
    toast(
      "Protege informa:",
      "PDF gerado, mas a URL pública não foi encontrada.",
      "info"
    );

    return;
  }

  window.open(url, "_blank");
}

function getCoverCustomFields() {
  const fields = effectiveLayout.value?.settings?.cover?.custom_fields || [];

  if (!Array.isArray(fields)) return [];

  return fields.map((field) => {
    return {
      key: field.key || field.id,
      id: field.id,
      type: "text",
      area: normalizeArea(field.area),
      value: field.value || "",
      label: field.label || "Campo personalizado",
      config: normalizeElementConfig(
        {
          key: field.key || field.id,
          type: "text",
        },
        field.config
      ),
    };
  });
}

function getCoverElementConfig(key, type = "text") {
  const config = effectiveLayout.value?.settings?.cover?.areas?.[key];

  return normalizeElementConfig(
    {
      key,
      type,
    },
    config
  );
}

function normalizeElementConfig(areaOption, config = null) {
  const defaultConfig = getDefaultElementConfig(areaOption);

  return {
    visible:
      typeof config?.visible === "boolean"
        ? config.visible
        : defaultConfig.visible,

    style: {
      ...defaultConfig.style,
      ...(config?.style || {}),
    },
  };
}

function getDefaultElementConfig(areaOption) {
  if (areaOption?.type === "image") {
    return {
      visible: true,
      style: {
        opacity: 1,
        shape: "square",
        objectFit: "contain",
        padding: 4,
        backgroundColor: "transparent",
        borderWidth: 0,
        borderColor: "#ffffff",
      },
    };
  }

  return {
    visible: true,
    style: {
      fontFamily: "Arial, Helvetica, sans-serif",
      fontSize: getDefaultFontSize(areaOption?.key),
      fontWeight: "700",
      color: effectiveLayout.value?.primary_color || "#111827",
      textAlign: "center",
      lineHeight: 1.15,
      letterSpacing: 0,
      textTransform: "none",
      opacity: 1,
    },
  };
}

function getDefaultFontSize(areaKey) {
  const sizes = {
    client_name_area: 20,
    client_document_area: 12,
    visit_date_area: 12,
    technician_name_area: 12,
  };

  if (String(areaKey || "").startsWith("custom_field_")) {
    return 28;
  }

  return sizes[areaKey] || 14;
}

function getAreaContentFrameStyle(dynamicArea) {
  if (dynamicArea.type !== "image") {
    return {};
  }

  const style = dynamicArea.config?.style || {};
  const shape = style.shape || "square";

  return {
    padding: `${Number(style.padding || 0)}px`,
    backgroundColor: style.backgroundColor || "transparent",
    border: `${Number(style.borderWidth || 0)}px solid ${
      style.borderColor || "transparent"
    }`,
    borderRadius: getShapeBorderRadius(shape),
    clipPath: getShapeClipPath(shape),
    opacity: String(style.opacity ?? 1),
    boxSizing: "border-box",
  };
}

function getImageAreaStyle(dynamicArea) {
  const style = dynamicArea.config?.style || {};

  return {
    objectFit: style.objectFit || "contain",
  };
}

function getShapeBorderRadius(shape) {
  const radiusMap = {
    square: "0",
    rounded: "18px",
    circle: "9999px",
    pill: "9999px",
    diamond: "0",
    hexagon: "0",
  };

  return radiusMap[shape] || "0";
}

function getShapeClipPath(shape) {
  const clipPathMap = {
    square: "none",
    rounded: "none",
    circle: "none",
    pill: "none",
    diamond: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
    hexagon:
      "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
  };

  return clipPathMap[shape] || "none";
}

</script>

<style scoped>
.preview-shell {
  display: flex;
  justify-content: center;
}

.preview-pages {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 48px;
}

.report-page {
  position: relative;
  width: 794px;
  min-height: 1123px;
  background: white;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
  border: 1px solid #e5e7eb;
}

.cover-page {
  height: 1123px;
}

.content-document {
  min-height: 1123px;
  padding: 118px 56px 96px;
}

.flow-content {
  position: relative;
  z-index: 2;
}

.report-intro {
  margin-bottom: 28px;
  padding-bottom: 22px;
  border-bottom: 1px solid #e5e7eb;
  break-inside: avoid;
  page-break-inside: avoid;
}

.intro-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.intro-meta div {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 12px;
  background: #f9fafb;
}

.intro-meta span {
  display: block;
  margin-bottom: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
}

.intro-meta strong {
  display: block;
  font-size: 12px;
  color: #374151;
}

.report-items-flow {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.report-item {
  position: relative;
  z-index: 2;
  padding: 22px;
  border: 1px solid #e5e7eb;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.96);
  break-inside: avoid;
  page-break-inside: avoid;
}

.item-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.item-photo {
  margin-bottom: 18px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #f9fafb;
  break-inside: avoid;
  page-break-inside: avoid;
}

.item-photo img {
  display: block;
  width: 100%;
  max-height: 360px;
  object-fit: cover;
}

.item-text-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 13px;
  line-height: 1.7;
  color: #374151;
}

.item-text-content section {
  break-inside: avoid;
  page-break-inside: avoid;
}

.empty-content {
  display: flex;
  min-height: 620px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.page-header-image {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.page-header-image img {
  width: 100%;
  display: block;
}

.page-header-basic {
  position: absolute;
  top: 34px;
  left: 56px;
  right: 56px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
  text-align: left;
}

.page-footer-image {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

.page-footer-image img {
  width: 100%;
  display: block;
}

.page-footer-basic {
  position: absolute;
  bottom: 28px;
  left: 56px;
  right: 56px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
  font-size: 11px;
  color: #9ca3af;
}

.watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  max-width: 65%;
  max-height: 65%;
  transform: translate(-50%, -50%);
  opacity: 0.08;
  pointer-events: none;
}

.section-title {
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #374151;
}

.report-item-large {
  break-inside: auto;
  page-break-inside: auto;
}

@media (max-width: 900px) {
  .report-page {
    width: 100%;
    min-height: auto;
  }

  .cover-page {
    aspect-ratio: 794 / 1123;
    height: auto;
  }

  .content-document {
    padding: 96px 24px 82px;
  }

  .item-header {
    flex-direction: column;
  }

  .intro-meta {
    grid-template-columns: 1fr;
  }
}

@media print {
  :global(body) {
    background: white !important;
  }

  .no-print {
    display: none !important;
  }

  .preview-shell {
    display: block;
  }

  .preview-pages {
    gap: 0;
    padding: 0;
  }

  .report-page {
    width: 210mm;
    min-height: 297mm;
    box-shadow: none;
    border: none;
  }

  .cover-page {
    height: 297mm;
    page-break-after: always;
    break-after: page;
  }

  .content-document {
    min-height: 297mm;
    padding: 32mm 15mm 26mm;
    page-break-after: auto;
    break-after: auto;
  }

  .report-item {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .item-photo {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .item-text-content section {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>
